import { Component, OnInit, OnDestroy, AfterViewInit, Input} from '@angular/core';
import { Router, NavigationStart } from "@angular/router";
import { Subscription, Observable, forkJoin } from "rxjs";
import { filter, tap } from "rxjs/operators";
//import { GridOptions, ColDef, GridApi, ColumnApi, RedrawRowsParams } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi, RedrawRowsParams } from "ag-grid-community";

import { OrganizationService } from "../../../../common/services/organization.service";
import { Organization } from "../../../../common/models/organization.model";
import { IssuesService, IssueSearchSpec } from "../../../../common/services/issues.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { Issue, IssueType, IssuesStatus } from "../../../../common/models/issue.model";
import { IssuesDataSource } from "../issues-widget/issues-grid-datasource";
import { AgGridBooleanFilterComponent } from '../../../../common/components/ag-grid/filters/boolean-filter.component';
import { IconActionRendererComponent } from "../../../../common/components/ag-grid/icon-action-renderer.component";
import { TabComponent } from "../../../../common/components/tabs/tab.component";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { ContactService } from "../../../../admin/services/contacts.service";
import { Contact } from "../../../../admin/models/contact.model";
import { OperationType } from "../../../../common/models/organization.model";

@Component({
    selector: 'issues-view',
    templateUrl: './issues-view.component.html',
    styleUrls: ['./issues-view.component.scss'],
    standalone: false
})
export class IssuesViewComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() widgetTitle: string;


    contacts: Contact[];

    dataPending: Observable<Issue[]>;
    dataProgress: Observable<Issue[]>;
    dataEscalated: Observable<Issue[]>;
    dataContested: Observable<Issue[]>;
    dataClosed: Observable<Issue[]>;

    errorDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { return true; })]);


	pendingGridOptions: GridOptions;
	pendingGridApi?: GridApi;
	progressGridOptions: GridOptions;
	progressGridApi?: GridApi;
	escalatedGridOptions: GridOptions;
	escalatedGridApi?: GridApi;
	contestedGridOptions: GridOptions;
	contestedGridApi?: GridApi;
	closedGridOptions: GridOptions;
	closedGridApi?: GridApi;

    apiColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "API #",
        headerTooltip: "API #",
        field: "wellId",
        valueGetter: this.formatter.formatPotentiallyBlankField("wellId", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellId", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 125,
    } as ColDef;

    assigneeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Assignee",
        headerTooltip: "Assignee",
        field: "assignee",
        valueGetter: this.formatAssignee("assignee"),
        tooltipValueGetter: this.formatAssignee("assignee"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    complianceNotice1Column: ColDef = {
        headerClass: "wrapped",
        headerName: "Compliance Notice 1 Received Date",
        headerTooltip: "Compliance Notice 1 Received Date",
        field: "complianceNotice1ReceivedDate",
        tooltipValueGetter: this.formatter.formatDate("complianceNotice1ReceivedDate"),
        valueGetter: this.formatter.formatDate("complianceNotice1ReceivedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    complianceNotice2Column: ColDef = {
        headerClass: "wrapped",
        headerName: "Compliance Notice 2 Received Date",
        headerTooltip: "Compliance Notice 2 REceived Date",
        field: "complianceNotice2ReceivedDate",
        tooltipValueGetter: this.formatter.formatDate("complianceNotice2ReceivedDate"),
        valueGetter: this.formatter.formatDate("complianceNotice2ReceivedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    closedDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date Closed",
        headerTooltip: "Date Closed",
        field: "resolutionDate",
        tooltipValueGetter: this.formatter.formatDate("resolutionDate"),
        valueGetter: this.formatter.formatDate("resolutionDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 135,
        maxWidth: 135,
        pinned: 'left'
    } as ColDef;

    createdDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date Created",
        headerTooltip: "Date Created",
        field: "creationDate",
        tooltipValueGetter: this.formatter.formatDate("creationDate"),
        valueGetter: this.formatter.formatDate("creationDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 110,
        maxWidth: 110,
        pinned: 'left'
    } as ColDef;

    descriptionColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Description",
        headerTooltip: "Issue Description",
        field: "description",
        tooltipField: "description",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
        maxWidth: 150
    } as ColDef;

    dueDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Due Date",
        headerTooltip: "Due Date",
        field: "dateDue",
        tooltipValueGetter: this.formatter.formatDate("dateDue"),
        valueGetter: this.formatter.formatDate("dateDue"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    editColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "",
        headerTooltip: "",
        field: "",
        filter: false,
        maxWidth: 60,
        minWidth: 60,
        cellStyle: { 'text-align': 'center', 'line-height': 1.75 },
        cellRenderer: IconActionRendererComponent,
        cellRendererParams: {
            buttons: [
                {
                    action: (obj) => this.editIssue(obj),
                    label: 'Edit Issue',
                    icon: 'edit'
                }
            ]
        },
        pinned: 'left'
    } as ColDef;


    approvalEditColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "",
        headerTooltip: "",
        field: "",
        filter: false,
        maxWidth: 60,
        minWidth: 60,
        cellStyle: { 'text-align': 'center', 'line-height': 1.75 },
        cellRenderer: IconActionRendererComponent,
        cellRendererParams: {
            buttons: [
                {
                    action: (obj) => this.approveIssue(obj),
                    label: 'Edit Issue',
                    icon: 'edit'
                }
            ]
        },
        pinned: 'left'
    } as ColDef;


    issueNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Number",
        headerTooltip: "Issue Number",
        field: "identifier",
        tooltipField: "identifier",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 120,
        pinned: 'left'
    } as ColDef;

    issueTypeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Code",
        headerTooltip: "Issue Code",
        field: "typeId",
        tooltipValueGetter: this.formatIssueType("typeId"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 75,
        maxWidth: 75,
        pinned: 'left'
    } as ColDef;

    letter1Column: ColDef = {
        headerClass: "wrapped",
        headerName: "Letter 1 Received Date",
        headerTooltip: "Letter 1 Received Date",
        field: "letter1ReceivedDate",
        tooltipValueGetter: this.formatter.formatDate("letter1ReceivedDate"),
        valueGetter: this.formatter.formatDate("letter1ReceivedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    letter2Column: ColDef = {
        headerClass: "wrapped",
        headerName: "Letter 2 Received Date",
        headerTooltip: "Letter 2 REceived Date",
        field: "letter2ReceivedDate",
        tooltipValueGetter: this.formatter.formatDate("letter2ReceivedDate"),
        valueGetter: this.formatter.formatDate("letter2ReceivedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    nextActionDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date of Next Action",
        headerTooltip: "Date of Next Action",
        field: "dateOfNextAction",
        tooltipValueGetter: this.formatter.formatDate("dateOfNextAction"),
        valueGetter: this.formatter.formatDate("dateOfNextAction"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    ownerNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Owner Name",
        headerTooltip: "Owner Name",
        field: "organizationNameAtTimeOfIssue",
        valueGetter: this.formatter.formatPotentiallyBlankField("organizationNameAtTimeOfIssue", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("organizationNameAtTimeOfIssue", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    ownerNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Owner Number",
        headerTooltip: "Owner Number",
        field: "organizationId",
        valueGetter: this.formatter.formatPotentiallyBlankField("organizationId", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("organizationId", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    programAreaColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Program Area",
        headerTooltip: "Program Area",
        field: "issueOwner",
        tooltipField: "issueOwner",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    registeredColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Registered in RICS?",
        headerTooltip: "Registered in RICS?",        
        tooltipValueGetter: this.formatRegistered("organizationId"),
        valueGetter: this.formatRegistered("organizationId"),
        filter: false,
        sortable: false,
        suppressHeaderMenuButton: true,
        minWidth: 150,
        maxWidth: 150
    } as ColDef;

    ownerTypeColumn: ColDef = {
        headerName: "Owner Type",
        headerTooltip: "Owner Type",
        tooltipValueGetter: this.formatOwnerType("organizationId"),
        valueGetter: this.formatOwnerType("organizationId"),
        filter: false,
        sortable: false,
        suppressHeaderMenuButton: true,
        minWidth: 110
    } as ColDef

    allGridColumns: ColDef[] = [
        this.createdDateColumn,
        this.issueTypeColumn,
        this.issueNumberColumn,
        this.descriptionColumn,
        this.ownerNumberColumn,
        this.ownerNameColumn,
        this.ownerTypeColumn,
        this.registeredColumn,
        this.programAreaColumn     
    ];

    startedGridColumns: ColDef[] = [
        this.assigneeColumn,
        this.nextActionDateColumn,
        this.dueDateColumn,
        this.apiColumn,
        this.letter1Column,
        this.letter2Column,
        this.complianceNotice1Column,
        this.complianceNotice2Column
    ];

    closedColumns: ColDef[] = [
        this.editColumn,
        this.createdDateColumn,
        this.closedDateColumn,
        this.issueTypeColumn,
        this.issueNumberColumn,
        this.descriptionColumn,
        this.ownerNumberColumn,
        this.ownerNameColumn,
        this.registeredColumn,
        this.programAreaColumn,
        this.dueDateColumn,
        this.apiColumn,
        this.letter1Column,
        this.letter2Column,
        this.complianceNotice1Column,
        this.complianceNotice2Column
    ];

    organizations: Organization[];
    issueTypes: IssueType[] = [];
    selectedTabName: string = "New";
    subscriptions: Subscription = new Subscription();

    constructor(public router: Router, private formatter: GridFormatterService, private issuesService: IssuesService, private organizationService: OrganizationService, private contactService: ContactService, private dialogService: DynamicDialogService) { }

    ngOnInit() {
        this.defineGridOptions();

        

        const typeObs$ = this.issuesService.getAllIssueTypes().pipe(
            tap((types: IssueType[]) => {
                this.issueTypes = types;
            })
        );

        const orgObs$ = this.organizationService.getAllExternalOrganizations().pipe(
            tap((orgs: Organization[]) => {
                this.organizations = orgs;
            })
        );

        const contact$ = this.contactService.getContactsByOrganization("0002").pipe(
            tap((contacts: Contact[]) => {
                this.contacts = contacts;
            }));

        const subs$ = forkJoin(typeObs$, orgObs$, contact$).pipe()
            .subscribe(
                (data: any[]) => {
                    this.redrawRows();
                },
                (err) => {
                    console.error(err);
                    this.showError("ERROR Fetching Issue Types");
                }
            );
        this.subscriptions.add(subs$);
        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        if (this.subscriptions) this.subscriptions.unsubscribe();
    }

    ngAfterViewInit() {
        this.sizeGrids();
    }

    approveIssue(data: any): void {
        this.router.navigate([`/issues/approve-escalation/${data.id}`]);
    }

    defineGridOptions(): void {
        const pendingColumns: ColDef[] = [];
        const progressColumns: ColDef[] = [];
        const escalatedColumns: ColDef[] = [];
        const contestedColumns: ColDef[] = [];        
        pendingColumns.push(this.dueDateColumn);
        pendingColumns.push(this.apiColumn);
        
        this.allGridColumns.forEach(c => {
            pendingColumns.push(c);
            progressColumns.push(c);
            escalatedColumns.push(c);
            contestedColumns.push(c);            
        });

        this.startedGridColumns.forEach(c => {
            progressColumns.push(c);
            escalatedColumns.push(c);
            contestedColumns.push(c);            
        });

        pendingColumns.unshift(this.editColumn);
        progressColumns.unshift(this.editColumn);
        escalatedColumns.unshift(this.approvalEditColumn);
        contestedColumns.unshift(this.editColumn);

        this.pendingGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(pendingColumns, true);
        this.pendingGridOptions.headerHeight = 50;
        this.progressGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(progressColumns, true);
        this.progressGridOptions.headerHeight = 50;
        this.escalatedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(escalatedColumns, true);
        this.escalatedGridOptions.headerHeight = 50;
        this.contestedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(contestedColumns, true);
        this.contestedGridOptions.headerHeight = 50;
        this.closedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.closedColumns, true);
        this.closedGridOptions.headerHeight = 50;

        this.pendingGridOptions.onGridReady = (params) => {
            const spec = {
                loadChildren: false,
                statuses: [IssuesStatus.Pending]
            } as IssueSearchSpec;
            this.pendingGridApi = params.api;
			const dataSource: IssuesDataSource = new IssuesDataSource(this.issuesService, this.pendingGridApi, spec, this.router);
			this.pendingGridApi.setGridOption("serverSideDatasource", dataSource);
			this.pendingGridApi.sizeColumnsToFit();
        };

        this.progressGridOptions.onGridReady = (params) => {
            const spec = {
                loadChildren: false,
                statuses: [IssuesStatus.Validated, IssuesStatus.Contested, IssuesStatus.Escalated]
            } as IssueSearchSpec;
            this.progressGridApi = params.api;
			const dataSource: IssuesDataSource = new IssuesDataSource(this.issuesService, this.progressGridApi, spec, this.router);
			this.progressGridApi.setGridOption("serverSideDatasource", dataSource);
			this.progressGridApi.sizeColumnsToFit();
        };

        this.escalatedGridOptions.onGridReady = (params) => {
            const spec = {
                loadChildren: false,
                statuses: [IssuesStatus.ChiefsOrderApproval]
            } as IssueSearchSpec;
            this.escalatedGridApi = params.api;
			const dataSource: IssuesDataSource = new IssuesDataSource(this.issuesService, this.escalatedGridApi, spec, this.router);

			this.escalatedGridApi.setGridOption("serverSideDatasource", dataSource);
			this.escalatedGridApi.sizeColumnsToFit();
        };

        this.contestedGridOptions.onGridReady = (params) => {
            const spec = {
                loadChildren: false,
                statuses: [IssuesStatus.Contested]
            } as IssueSearchSpec;
            this.contestedGridApi = params.api;
			const dataSource: IssuesDataSource = new IssuesDataSource(this.issuesService, this.contestedGridApi, spec, this.router);

			this.contestedGridApi.setGridOption("serverSideDatasource", dataSource);
			this.contestedGridApi.sizeColumnsToFit();
        };

        this.closedGridOptions.onGridReady = (params) => {
            const spec = {
                loadChildren: false,
                statuses: [IssuesStatus.Closed]
            } as IssueSearchSpec;
            this.closedGridApi = params.api;
			const dataSource: IssuesDataSource = new IssuesDataSource(this.issuesService, this.closedGridApi, spec, this.router);

			this.closedGridApi.setGridOption("serverSideDatasource", dataSource);
			this.closedGridApi.sizeColumnsToFit();
        };
    }

    editIssue(data: any): void {
        this.router.navigate([`/issues/review-issue/${data.id}`]);
    }

    formatAssignee(field: string): any {
        return (obj) => {
            let assignee = "N/A";
            if (obj.data != null && obj.data[field] != null) {
                assignee = obj.data[field];
                if (this.contacts != null) {
                    const contact = this.contacts.find(c => c.id === assignee);
                    if (contact != null) assignee = contact.displayName;
                }
            }
            return assignee;
        }
    }

    formatIssueType(field: string): any {
        return (obj) => {
            let returnValue = "N/A";
            if (obj.data && obj.data[field] != undefined && obj.data[field] != null) {
                let matchingIssueTypes = this.issueTypes.filter(a => a.id == obj.data[field]);
                if (matchingIssueTypes && matchingIssueTypes.length > 0)
                    returnValue = "(" + matchingIssueTypes[0].id + ") " + matchingIssueTypes[0].description;
            }

            return returnValue;
        }
    }

    formatOwnerType(field: string): any {
        return (obj) => {
            let returnValue: string = "";
            if (this.organizations == null) {
                returnValue = "Loading";
            } else if (obj.data != null && obj.data[field] != null) {
                const value = obj.data[field].toString();
                let org: Organization;
                if (value.startsWith("ON")) {
                    org = this.organizations.find(o => o.id === value);
                } else {
                    let hist = this.organizations.filter(o => o.historicalOwnerNumber === value);
                    if (hist.length === 0) {
                        hist = this.organizations.filter(o => o.id === value);
                    }
                    if (hist.length > 0) {
                        org = hist.find(h => h.hasRegistered === true);
                        if (org == null) org = hist[0];
                    }
                }
                if (org != null) {
                    let wellOwner: boolean = ((org.operationType & OperationType.Operator) !== 0 || (org.operationType & OperationType.InjectionOperator) !== 0);
                    let brineHauler: boolean = (org.operationType & OperationType.BrineHauler) !== 0;
                    if (brineHauler && wellOwner) {
                        returnValue = "Brine Hauler, Well Owner";
                    } else if (brineHauler) {
                        returnValue = "Brine Hauler";
                    } else if (wellOwner) {
                        returnValue = "Well Owner";
                    }
                }
            }
            return returnValue;
        }
    }

    formatRegistered(field: string): any {
        return (obj) => {
            let returnValue = "No";
            if (this.organizations == null) {
                returnValue = "Loading";
            } else if (obj.data && obj.data[field] != null) {
                const value = obj.data[field].toString();
                let found: boolean = false;
                if (value.startsWith("ON")) {
                    const org = this.organizations.find(o => o.id === value);
                    returnValue = (org != null && org.hasRegistered) ? "Yes" : "No";
                    found = org != null;
                } else {
                    let hist = this.organizations.filter(o => o.historicalOwnerNumber === value);
                    if (hist.length === 0) {
                        hist = this.organizations.filter(o => o.id === value);
                        found = true;
                    }
                    returnValue = (hist.length > 0 && hist.find(h => h.hasRegistered === true)) ? "Yes" : "No";
                }
            }
            return returnValue;
        }
    }

    onTabClicked(tab: TabComponent): void {
        this.selectedTabName = tab.title;
        this.sizeGrids();
    }

    redrawRows(): void {
        switch (this.selectedTabName) {
            case "New":
                if (this.pendingGridOptions && this.pendingGridApi) {
                    var params = <RedrawRowsParams>[];
                    this.pendingGridApi.redrawRows(params);
                    this.pendingGridApi.sizeColumnsToFit();
                }
                break;
            case "In Progress":
                if (this.progressGridOptions && this.progressGridApi) {
                    var params = <RedrawRowsParams>[];
                    this.progressGridApi.redrawRows(params);
                    this.progressGridApi.sizeColumnsToFit();
                }
                break;
            case "Escalation Review":
                if (this.escalatedGridOptions && this.escalatedGridApi) {
                    var params = <RedrawRowsParams>[];
                    this.escalatedGridApi.redrawRows(params);
                    this.escalatedGridApi.sizeColumnsToFit();
                }
                break;
            case "Contested":
                if (this.contestedGridOptions && this.contestedGridApi) {
                    var params = <RedrawRowsParams>[];
                    this.contestedGridApi.redrawRows(params);
                    this.contestedGridApi.sizeColumnsToFit();
                }
                break;
            case "Closed":
                if (this.closedGridOptions && this.closedGridApi) {
                    var params = <RedrawRowsParams>[];
                    this.closedGridApi.redrawRows(params);
                    this.closedGridApi.sizeColumnsToFit();
                }
                break;
        }
    }

    showError(msg: string): void {
        this.errorDialog.message = msg;
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    sizeGrids(): void {
        switch (this.selectedTabName) {
            case "New":
                if (this.pendingGridApi) this.pendingGridApi.sizeColumnsToFit();
                break;
            case "In Progress":
                if (this.progressGridApi) this.progressGridApi.sizeColumnsToFit();
                break;
            case "Escalation Review":
                if (this.escalatedGridApi) this.escalatedGridApi.sizeColumnsToFit();
                break;
            case "Contested":
                if (this.contestedGridApi) this.contestedGridApi.sizeColumnsToFit();
                break;
            case "Closed":
                if (this.closedGridApi) this.closedGridApi.sizeColumnsToFit();
                break;
        }
    }
}
